import type {
  BackofficeVoucherListItem,
  VoucherDiscountProduct,
} from '@diamond/shared/types';

import type { VoucherFilters, VoucherListItem, VoucherStatus } from '../types';
import { DEFAULT_PRODUCT_ITEM_CODE } from './constants';

/**
 * Maps discount products based on reward type
 * Creates backward-compatible discount product structure
 */
const mapDiscountProducts = (
  rewardType: VoucherListItem['rewardConfiguration']['reward_type'],
  rewardConfig: BackofficeVoucherListItem['reward_configuration']
): VoucherDiscountProduct[] => {
  if (rewardType === 'DISCOUNT_PERCENT') {
    return [
      {
        product_item_code: DEFAULT_PRODUCT_ITEM_CODE,
        discount_percentage: rewardConfig.discount_percentage ?? 0,
        discount_amount: 0,
      },
    ];
  }

  if (rewardType === 'DISCOUNT_AMOUNT') {
    return [
      {
        product_item_code: DEFAULT_PRODUCT_ITEM_CODE,
        discount_percentage: 0,
        discount_amount: rewardConfig.discount_amount ?? 0,
      },
    ];
  }

  return [];
};

/**
 * Maps BackofficeVoucherListItem to VoucherListItem
 * Normalizes API response to frontend data structure
 */
export const mapVoucherToListItem = (
  voucher: BackofficeVoucherListItem
): VoucherListItem => {
  const normalizedStatus = voucher.status.toUpperCase() as VoucherStatus;
  const normalizedRewardType =
    voucher.reward_configuration.reward_type.toUpperCase() as VoucherListItem['rewardConfiguration']['reward_type'];

  return {
    id: voucher.id,
    code: voucher.code,
    title: voucher.title,
    desc: voucher.desc ?? undefined,
    bannerUrl: voucher.banner_url ?? null,
    periodStart: voucher.period_start,
    periodEnd: voucher.period_end,
    status: normalizedStatus,
    isDisplay: Boolean(voucher.is_display),
    displayOrder: voucher.display_order ?? 0,
    rewardConfiguration: {
      reward_type: normalizedRewardType,
      is_bundling: voucher.reward_configuration.is_bundling ?? false,
      min_total_quantity_all_item:
        voucher.reward_configuration.min_total_quantity_all_item ?? null,
      reward_rule_products:
        voucher.reward_configuration.reward_rule_products ?? [],
      discount_products: mapDiscountProducts(
        normalizedRewardType,
        voucher.reward_configuration
      ),
      free_goods: voucher.reward_configuration.free_goods ?? [],
    },
    targetConfiguration: {
      quota_total: voucher.target_configuration.quota_total ?? 0,
      quota_used: voucher.target_configuration.quota_used ?? 0,
      distribution_channels: {
        count: voucher.target_configuration.distribution_channels.count ?? 0,
        is_all:
          voucher.target_configuration.distribution_channels.is_all ?? true,
        items: voucher.target_configuration.distribution_channels.items ?? [],
      },
      cg1_business_segmentations:
        voucher.target_configuration.cg1_business_segmentations ?? [],
      cg2_business_categories:
        voucher.target_configuration.cg2_business_categories ?? [],
    },
    createdAt: voucher.created_at,
    updatedAt: voucher.updated_at,
  };
};

/**
 * Sanitizes filter parameters
 * Removes empty/undefined values and normalizes filter structure for API
 */
export const sanitizeVoucherFilters = (
  filters?: VoucherFilters
): Record<string, string | number | boolean> | undefined => {
  if (!filters) return undefined;

  const params: Record<string, string | number | boolean> = {};

  if (typeof filters.page === 'number') params.page = filters.page;
  if (typeof filters.size === 'number') params.size = filters.size;
  if (filters.search_query) params.search_query = filters.search_query;
  if (typeof filters.start_date === 'number')
    params.start_date = filters.start_date;
  if (typeof filters.end_date === 'number') params.end_date = filters.end_date;
  if (filters.status !== '' && filters.status) params.status = filters.status;
  if (filters.reward_type !== '' && filters.reward_type)
    params.reward_type = filters.reward_type;
  if (filters.mechanism !== '' && filters.mechanism)
    params.mechanism = filters.mechanism;
  if (typeof filters.is_display === 'boolean')
    params.is_display = filters.is_display;

  return params;
};
